// File: src/components/QuestionInput/azureSpeechBackend.ts
//
// Azure Speech backend for the mic button, used when the gateway (ENG-008) selects it via
// GET /speech/token. Loaded only on the first mic tap through a dynamic import() so the
// Azure Speech SDK for JavaScript never lands in the widget's initial bundle — it gets its
// own chunk (see vite.widget.config.ts manualChunks).
//
// This exists because the Genie Android WebView has no Web Speech API at all: getUserMedia
// plus this backend is the only way the mic works there (see useSpeechInput.ts `supported`).

import type { SpeechNotice, SpeechUsageOutcome } from "./useSpeechInput";
import type { MicLevel } from "./micLevel";

// Mirrors the ENG-008 gateway contract for GET /speech/token.
export type SpeechTokenResponse = {
    token: string | null;
    endpoint: string;
    region: string | null;
    expiresInSeconds: number;
    language: string;
    phrases: string[];
    backend: "auto" | "browser" | "azure";
    // ENG-013: admin-managed silence timeouts (ms). Optional — an older gateway omits them,
    // in which case the defaults below apply.
    endSilenceMs?: number;
    initialSilenceMs?: number;
};

// Used when the gateway predates ENG-013 and sends no timeouts.
const DEFAULT_END_SILENCE_MS = 1500;
const DEFAULT_INITIAL_SILENCE_MS = 5000;

const timeoutOrDefault = (value: number | undefined, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.round(value) : fallback;

export type AzureSpeechCallbacks = {
    onInterim: (text: string) => void;
    onFinal: (text: string) => void;
    onNotice: (notice: SpeechNotice) => void;
    // Called exactly once, whatever the outcome (success, no-speech, cancellation, or a stop/abort
    // call) — with the ENG-012 usage outcome (a superset of SpeechNotice) and the length of
    // whatever was recognized so far, for the /speech/usage ping.
    onEnd: (outcome: SpeechUsageOutcome, transcriptLength: number) => void;
};

export type AzureSpeechHandle = {
    // recognizeOnceAsync has no "finish early, keep the partial result" API the way the
    // Web Speech API's stop() does — both stop and abort close the recognizer outright.
    stop: () => void;
    abort: () => void;
    // ENG-071: the mic level for the bars, 0..1, or null when there is none to show (the SDK had to
    // open the mic itself, or the meter's AudioContext is not running): the bars then pulse.
    readLevel: () => number | null;
};

// Starts one single-utterance Azure recognition (recognizeOnceAsync), same shape as the
// browser backend: it stops on its own at silence. Returns null (after calling onNotice +
// onEnd once) if setup failed before a recognizer could even be created.
export const startAzureSpeechRecognition = async (token: SpeechTokenResponse, callbacks: AzureSpeechCallbacks): Promise<AzureSpeechHandle | null> => {
    // ENG-012: length of whatever was recognized so far, for the /speech/usage ping — never
    // the text itself, only its length.
    let transcriptLength = 0;

    let sdk: typeof import("microsoft-cognitiveservices-speech-sdk");
    try {
        sdk = await import("microsoft-cognitiveservices-speech-sdk");
    } catch (error) {
        console.warn("[speech] error", "sdk-load", error);
        callbacks.onNotice("service");
        callbacks.onEnd("sdk-load", 0);
        return null;
    }

    // Probe the microphone ourselves first. getUserMedia's own error name gives a much
    // clearer permission/device signal than the SDK's CancellationErrorCode does once the
    // recognizer is already talking to the service.
    // ENG-071: and keep that stream: the SDK records from it (fromStreamInput below) and the level
    // bars listen to the same one, so the device is opened once. Exactly { audio: true }, which is
    // what the SDK itself asks for, so the browser's echo cancellation, noise suppression and auto
    // gain stay as they were before.
    let micStream: MediaStream;
    try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
        const name = error instanceof DOMException ? error.name : "";
        console.warn("[speech] error", "getUserMedia", name || error);
        let notice: SpeechNotice;
        if (name === "NotAllowedError" || name === "SecurityError") {
            notice = "permission";
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
            notice = "no-device";
        } else {
            notice = "service";
        }
        callbacks.onNotice(notice);
        callbacks.onEnd(notice, 0);
        return null;
    }

    // ENG-071: every way this attempt ends comes through here, so the browser's (or Android's)
    // "microphone in use" indicator goes off: the SDK never stops the tracks of a stream it was
    // given, and the bars' AudioContext is ours to close too.
    let level: MicLevel | null = null;
    const releaseMic = () => {
        micStream.getTracks().forEach(track => track.stop());
        level?.close();
        level = null;
    };

    // ENG-071: false when the SDK refused our stream and opened the mic itself (no level, bars pulse).
    let streamInput = true;
    let recognizer: InstanceType<typeof sdk.SpeechRecognizer>;
    try {
        const speechConfig = sdk.SpeechConfig.fromEndpoint(new URL(token.endpoint));
        // fromEndpoint()'s subscriptionKey argument is optional; an authorization token is
        // set separately below. Verified against the installed 1.51.0 typings — no need for
        // the fromAuthorizationToken(token, region) fallback the ticket flagged as a possibility.
        if (token.token) {
            speechConfig.authorizationToken = token.token;
        }
        speechConfig.speechRecognitionLanguage = token.language;
        // ENG-013: how long a pause may last before the utterance is considered finished, so a
        // salesperson thinking mid-sentence isn't cut off. SpeechServiceConnection_EndSilenceTimeoutMs
        // is the name the ticket asked for; the 1.51.0 typings mark it deprecated in favour of
        // Speech_SegmentationSilenceTimeoutMs (which is what ends a phrase in recognizeOnceAsync), so
        // both are set to the same value — the SDK still sends each one to the service.
        const endSilenceMs = String(timeoutOrDefault(token.endSilenceMs, DEFAULT_END_SILENCE_MS));
        speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, endSilenceMs);
        speechConfig.setProperty(sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs, endSilenceMs);
        speechConfig.setProperty(
            sdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
            String(timeoutOrDefault(token.initialSilenceMs, DEFAULT_INITIAL_SILENCE_MS))
        );

        // ENG-071: the SDK records from our stream. Should it refuse it, it opens the mic itself as
        // it did before ENG-071 (our tracks stop first, so the device is still opened once at a time).
        let audioConfig: ReturnType<typeof sdk.AudioConfig.fromStreamInput>;
        try {
            audioConfig = sdk.AudioConfig.fromStreamInput(micStream);
        } catch (error) {
            console.warn("[speech] error", "stream-input", error);
            streamInput = false;
            micStream.getTracks().forEach(track => track.stop());
            audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
        }
        recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

        if (token.phrases.length > 0) {
            const phraseList = sdk.PhraseListGrammar.fromRecognizer(recognizer);
            token.phrases.forEach(phrase => phraseList.addPhrase(phrase));
        }
    } catch (error) {
        console.warn("[speech] error", "setup", error);
        releaseMic();
        callbacks.onNotice("service");
        callbacks.onEnd("service", 0);
        return null;
    }

    let ended = false;
    // Default outcome if finish() is reached without any other branch below setting it first —
    // that only happens when the caller's stop()/abort() handle is invoked directly (recognizeOnceAsync
    // has no "keep the partial result" stop, so re-tapping the mic to stop is itself an abort/cancel).
    let outcome: SpeechUsageOutcome = "aborted";

    // ENG-071: the SDK picks our stream up a few ms after recognizeOnceAsync(). Closed before that
    // (an abort or an unmount right at the start) it starts anyway, finds the stream already
    // stopped and opens the mic itself, and nothing would stop that one. So the recognizer and the
    // mic close once the SDK has settled: its session started, it was canceled, or
    // recognizeOnceAsync called back. The attempt itself still ends at once (onEnd below).
    let sdkSettled = false;
    let closed = false;
    const closeRecognizer = () => {
        if (closed || !sdkSettled) {
            return;
        }
        closed = true;
        recognizer.close();
        releaseMic();
    };
    const settleSdk = () => {
        sdkSettled = true;
        if (ended) {
            closeRecognizer();
        }
    };

    const finish = () => {
        if (ended) {
            return;
        }
        ended = true;
        closeRecognizer();
        callbacks.onEnd(outcome, transcriptLength);
    };

    recognizer.sessionStarted = () => settleSdk();

    recognizer.recognizing = (_sender, event) => {
        transcriptLength = event.result.text.length;
        callbacks.onInterim(event.result.text);
    };

    recognizer.canceled = (_sender, event) => {
        settleSdk();
        console.warn("[speech] error", event.errorCode, event.errorDetails);
        if (event.reason === sdk.CancellationReason.Error) {
            const code = event.errorCode;
            const notice: SpeechNotice =
                code === sdk.CancellationErrorCode.AuthenticationFailure ||
                code === sdk.CancellationErrorCode.ConnectionFailure ||
                code === sdk.CancellationErrorCode.ServiceTimeout
                    ? "network"
                    : "service";
            outcome = notice;
            callbacks.onNotice(notice);
        }
        finish();
    };

    console.info(`[speech] start backend=azure lang=${token.language}`);

    recognizer.recognizeOnceAsync(
        result => {
            settleSdk();
            if (ended) {
                return;
            }
            if (result.reason === sdk.ResultReason.NoMatch) {
                outcome = "no-speech";
                callbacks.onNotice("no-speech");
            } else if (result.reason === sdk.ResultReason.RecognizedSpeech) {
                outcome = "ok";
                transcriptLength = result.text.length;
                callbacks.onFinal(result.text);
            }
            finish();
        },
        error => {
            settleSdk();
            console.warn("[speech] error", "recognizeOnceAsync", error);
            outcome = "network";
            callbacks.onNotice("network");
            finish();
        }
    );

    // ENG-071: the bars start after the recognizer and are never awaited, so they cannot hold
    // recognition up; any failure just leaves level null (the bars pulse). micLevel.ts is in the
    // SDK's chunk, which has already loaded by now.
    if (streamInput) {
        void import("./micLevel")
            .then(({ startMicLevel }) => {
                if (!ended) {
                    level = startMicLevel(micStream);
                }
            })
            .catch(error => console.warn("[speech] error", "mic-level", error));
    }

    return {
        stop: () => finish(),
        abort: () => finish(),
        readLevel: () => level?.read() ?? null
    };
};
